import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import {
  Button,
  Tooltip,
  Collapse,
  Switch,
  InputNumber,
  Tag,
  Popconfirm,
  Modal,
  Select,
} from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCircleInfo,
  faCircleQuestion,
  faCode,
  faDatabase,
  faNetworkWired,
  faPen,
  faPlus,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import SelectLn2Message from "../../../../components/select/SelectLn2Message";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import DataValueEditor from "./DataValueEditor";
import AttributeSelect, { attributeLabel } from "./AttributeSelect";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import { categoryColor, categoryBorderColor } from "./mappingCategoryColor";
import {
  countReplacedByRange,
  expandFloorRange,
  floorRangeProblem,
  maxFloorPosition,
  replaceRange,
} from "./floorRange";
import {
  floorTemplateProblem,
  floorTemplateSummary,
  type FloorTemplateContext,
} from "./floorTemplate";
// FLATBUFFER ENUMS
import { AttributeId } from "../../../../../flatbuffers/generated/opc-ua/attribute-id";
import { opcUaMappingRowToCdoEntry } from "../../../../../flatbuffers/builders/opcUaCdoBuilder";
import humanizeCdoEntry from "../../../../../flatbuffers/tools/cdoEntryHumanizer";
// INTERFACES
import type {
  OpcUaFloorExpansionForm,
  OpcUaFloorExpansionMode,
  OpcUaNodeIdForm,
  OpcUaNodeMonitorForm,
  OpcUaNodeToMessageRow,
  OpcUaMessageToNodeRow,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";
// UTILS
import { describeUnbindableNode, opcUaNodeIdString } from "../../../../../utils/opcUaNodeId";
import {
  getDisplayName,
  getCategory,
  getOptionLabel,
  messageCarriesPosition,
} from "../../../../../flatbuffers/types/ln2MessageOptions";

const { Panel } = Collapse;

const BLANK_NODE: OpcUaNodeIdForm = BLANK_NUMERIC_NODE_ID;
const BLANK_VALUE = { kind: "string" as const, value: "" };

const BLANK_NODE_TO_MSG: OpcUaNodeToMessageRow = {
  node: { ...BLANK_NODE },
  nodeValue: { ...BLANK_VALUE },
  attributeId: AttributeId.Value,
  msgId: "",
  active: true,
  position: null,
  floorExpansion: null,
};

const BLANK_MSG_TO_NODE: OpcUaMessageToNodeRow = {
  msgId: "",
  active: true,
  node: { ...BLANK_NODE },
  nodeValue: { ...BLANK_VALUE },
  attributeId: AttributeId.Value,
  position: null,
  floorExpansion: null,
};

/** True when a NodeId form has a non-empty identifier (or, for a BrowsePath, at least one hop) — the minimum bar for a row to be addable. */
function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

/** Canonical description of a NodeId for display in the editor.
 *
 *  For every kind that has one, this is the real OPC UA `NodeId` string
 *  (`ns=2;s=Lift1.Position`) rather than a paraphrase of it, so what the
 *  operator reads here is what is actually sent. Kinds with no `NodeId`
 *  encoding fall back to a readable form.
 *
 *  This is a **display** helper. It is deliberately no longer the source of
 *  the bank binding's wire keys — those come from `opcUaNodeIdString`, whose
 *  spelling is a contract with LiftCloud's parser. Keeping one function for
 *  both jobs is what let the two sides disagree silently. */
export function describeNode(node: OpcUaNodeIdForm): string {
  return opcUaNodeIdString(node) ?? describeUnbindableNode(node);
}

/** Largest floor `position` can hold: it is a uint16 on the wire. */
export const MAX_FLOOR_POSITION = 65535;

/**
 * The floor a row should hold once `msgId` is its message.
 *
 * A message that carries a position (every elevator Traffic and Control
 * message) always has a floor on the wire: `position` is a uint16, so there is
 * no "no floor" value and a row without one ships as floor 0. Such a row
 * therefore keeps the floor already staged, or starts at 0 so the operator sees
 * the value that will be sent. Any other message has no `position` field, so its
 * row holds null and nothing is written.
 */
function positionForMessage(msgId: string, staged: number | null): number | null {
  if (!messageCarriesPosition(msgId)) return null;
  return staged ?? 0;
}

/**
 * True when a row whose message carries a position is missing its floor. A floor
 * template is the exception: it has no floor of its own, LiftCloud stamps each
 * expanded entry with its own.
 */
function isFloorMissing(row: {
  msgId: string;
  position: number | null;
  floorExpansion: OpcUaFloorExpansionForm | null;
}): boolean {
  return row.floorExpansion === null && messageCarriesPosition(row.msgId) && row.position === null;
}

/**
 * The floor a mapping row addresses, shown only for a message that carries one
 * (see `positionForMessage`).
 *
 * The value is the LN2 floor index, counted from 0 at the lowest floor. VTS
 * forwards the mapped message unchanged and applies no vendor floor offset, so
 * the adapter's Vendor Floor Indexing setting does not apply here.
 *
 * The floor is required for these messages. Clearing the input leaves the row
 * without one, which blocks Add and shows why, rather than letting an empty box
 * stand for the floor 0 the builder would actually send.
 */
function PositionField({
  value,
  onChange,
  disabled = false,
  range = undefined,
}: {
  value: number | null;
  onChange: (position: number | null) => void;
  disabled?: boolean;
  /** Only the create form offers a range; an in-place edit addresses one stored row. */
  range?: PositionRangeControl;
}) {
  const [t] = useTranslation("global");
  const missing = value === null;
  // One dedicated row: the floor input takes the whole width; when a range is
  // on, it and "To Floor" split it 50/50. The switch stays at the right end of
  // the same row either way, so nothing below it shifts when it is toggled.
  return (
    <div className="row mt-2">
      <div className="col-12 mt-2">
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="generalStyles__inputLabel">
              {t("general.opcUaMappingFloor")}
              <Tooltip title={t("general.opcUaMappingFloorHelp")}>
                <FontAwesomeIcon icon={faCircleInfo} className="generalStyles__mlFix" />
              </Tooltip>
            </span>
            <InputNumber
              data-doc-label="Floor (position)"
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              min={0}
              max={MAX_FLOOR_POSITION}
              precision={0}
              status={missing ? "error" : undefined}
              value={value}
              disabled={disabled}
              onChange={(next) => onChange(typeof next === "number" ? next : null)}
            />
            {missing && (
              <div role="alert" style={{ color: "#ff4d4f" }}>
                {t("general.opcUaMappingFloorRequired")}
              </div>
            )}
          </div>
          {range?.enabled && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className="generalStyles__inputLabel">
                {range.bounded ? `To Floor (Max ${range.max})` : "To Floor"}
              </span>
              <InputNumber
                data-doc-label="To floor"
                className="generalStyles__width100"
                size="large"
                style={{ width: "100%", marginTop: 8 }}
                min={range.min}
                max={range.max}
                precision={0}
                status={range.endInvalid ? "error" : undefined}
                value={range.end}
                disabled={disabled}
                onChange={(next) => range.onChangeEnd(typeof next === "number" ? next : null)}
              />
            </div>
          )}
          {range && (
            <div style={{ flex: "0 0 auto", textAlign: "center" }}>
              <span className="generalStyles__inputLabel">Create Range</span>
              <div style={{ marginTop: 8, height: 40, display: "flex", alignItems: "center" }}>
                <Switch
                  aria-label="Create Range"
                  checked={range.enabled}
                  onChange={range.onToggle}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
        </div>
        {range?.enabled && range.problem !== null && (
          <div role="alert" style={{ color: "#ff4d4f" }}>
            {range.problem}
          </div>
        )}
        {range?.enabled && range.problem === null && range.replaceCount > 0 && (
          <div style={{ color: "#faad14", fontSize: 12 }}>
            {range.replaceCount} existing row(s) of this message in this range will be replaced.
          </div>
        )}
      </div>
    </div>
  );
}

/** What the create form needs to offer "Create Range" next to the floor input. */
interface PositionRangeControl {
  enabled: boolean;
  /** Last floor of the range; null while empty. */
  end: number | null;
  /** Highest floor allowed: the adapter's last floor when its count is known, else the wire limit. */
  max: number;
  /** True when `max` comes from a known floor count; false = only the wire limit applies and the label omits it. */
  bounded: boolean;
  /** Lowest floor the range may end on: the floor it starts at. */
  min: number;
  /** True when the To Floor value itself is the problem (empty, below the start, above the max), as opposed to e.g. the Node Identifier. */
  endInvalid: boolean;
  /** Why the range cannot be created yet, or null when it can. */
  problem: string | null;
  /** Stored rows of the same message inside the range, which creating it replaces. */
  replaceCount: number;
  onToggle: (enabled: boolean) => void;
  onChangeEnd: (end: number | null) => void;
}

type MappingFormValue = OpcUaNodeToMessageRow | OpcUaMessageToNodeRow;

/**
 * `draft` with `msgId` as its message, and the floor state that message can
 * hold: a message that carries no floor drops both a staged floor and any floor
 * template, and a floor-carrying message that is not a template starts at floor 0.
 */
function withMessage<T extends MappingFormValue>(draft: T, msgId: string): T {
  const floorExpansion = messageCarriesPosition(msgId) ? draft.floorExpansion : null;
  return {
    ...draft,
    msgId,
    floorExpansion,
    position: floorExpansion === null ? positionForMessage(msgId, draft.position) : null,
  };
}

/**
 * What a floor template previews against: the bank's floor count, and the
 * adapter's floor numbering when the caller knows it.
 */
interface FloorTemplateSetup {
  context: FloorTemplateContext;
  /** True when `context.span` is the editor's own sample, because the adapter's floor count is unknown. */
  spanIsSample: boolean;
}

/** Floors the preview shows when the adapter's own count is unknown. */
const SAMPLE_FLOOR_SPAN = 10;

/** The "Expand by" choices, in the order they are offered. */
const FLOOR_EXPANSION_MODE_OPTIONS: { value: OpcUaFloorExpansionMode; label: string }[] = [
  { value: "dataOnly", label: "Value" },
  { value: "pathOnly", label: "Node path" },
  { value: "pathAndData", label: "Value and node path" },
];

/** What turning "Expand across floors" on starts from: the value varies, with no base set. */
const DEFAULT_FLOOR_EXPANSION: OpcUaFloorExpansionForm = {
  mode: "dataOnly",
  valueBase: 0,
  pathBase: 0,
};

/** Wire limits of the generated `FloorExpansion` bases (int32). */
const FLOOR_BASE_MIN = -2147483648;
const FLOOR_BASE_MAX = 2147483647;

/**
 * The row badge's tooltip, one whole phrase per mode. They are spelled out rather
 * than built around the mode's label so each reads, and can be translated, as a
 * sentence of its own.
 */
const FLOOR_TEMPLATE_BADGE_TITLE: Record<OpcUaFloorExpansionMode, string> = {
  dataOnly: "Floor template: expanded across floors by value",
  pathOnly: "Floor template: expanded across floors by node path",
  pathAndData: "Floor template: expanded across floors by value and node path",
};

/**
 * "Expand across floors": author this row once and let LiftCloud write one
 * entry per floor, instead of one row (or a Create Range) per floor. Shown only
 * for a message that carries a floor. While it is on the row has no Floor of its
 * own and no range; the controls below it pick what varies, the preview shows
 * what it expands to, and a problem blocks Add / Save.
 */
function FloorTemplateField<T extends MappingFormValue>({
  draft,
  setup,
  onChange,
  disabled = false,
}: {
  draft: T;
  setup: FloorTemplateSetup;
  onChange: (floorExpansion: OpcUaFloorExpansionForm | null) => void;
  disabled?: boolean;
}) {
  const expansion = draft.floorExpansion;
  const problem = expansion === null ? null : floorTemplateProblem(draft, setup.context);
  return (
    <div className="row mt-2">
      <div className="col-12 mt-2">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: "0 0 auto", textAlign: "center" }}>
            <OpcUaFieldLabel
              label="Expand across floors"
              tooltip="Author this row once and let LiftCloud write one entry per floor when it builds the bank's configuration, instead of one row per floor."
            />
            <div style={{ marginTop: 8, height: 40, display: "flex", alignItems: "center" }}>
              <Switch
                aria-label="Expand across floors"
                checked={expansion !== null}
                onChange={(on) => onChange(on ? { ...DEFAULT_FLOOR_EXPANSION } : null)}
                disabled={disabled}
              />
            </div>
          </div>
        </div>
        {expansion !== null && (
          <>
            <div className="row">
              <div className="col-12 col-md-4 mt-2">
                <OpcUaFieldLabel
                  label="Expand by"
                  tooltip="What differs from one floor to the next: the value the node reports, the node's path, or both."
                />
                <Select
                  data-doc-label="Expand by"
                  className="generalStyles__width100"
                  size="large"
                  style={{ width: "100%", marginTop: 8 }}
                  value={expansion.mode}
                  options={FLOOR_EXPANSION_MODE_OPTIONS}
                  disabled={disabled}
                  onChange={(mode: OpcUaFloorExpansionMode) => onChange({ ...expansion, mode })}
                />
              </div>
              {expansion.mode !== "pathOnly" && (
                <div className="col-12 col-md-4 mt-2">
                  <OpcUaFieldLabel
                    label="First value"
                    tooltip="The value floor 0 reports. Floor i reports i plus this. Leave it 0 to take the adapter's floor numbering instead."
                  />
                  <InputNumber
                    data-doc-label="First value"
                    className="generalStyles__width100"
                    size="large"
                    style={{ width: "100%", marginTop: 8 }}
                    min={FLOOR_BASE_MIN}
                    max={FLOOR_BASE_MAX}
                    precision={0}
                    value={expansion.valueBase}
                    disabled={disabled}
                    onChange={(next) =>
                      onChange({ ...expansion, valueBase: typeof next === "number" ? next : 0 })
                    }
                  />
                </div>
              )}
              {expansion.mode !== "dataOnly" && (
                <div className="col-12 col-md-4 mt-2">
                  <OpcUaFieldLabel
                    label="First path number"
                    tooltip="The number that replaces {floor} in the Node Identifier on floor 0. Floor i uses i plus this."
                  />
                  <InputNumber
                    data-doc-label="First path number"
                    className="generalStyles__width100"
                    size="large"
                    style={{ width: "100%", marginTop: 8 }}
                    min={FLOOR_BASE_MIN}
                    max={FLOOR_BASE_MAX}
                    precision={0}
                    value={expansion.pathBase}
                    disabled={disabled}
                    onChange={(next) =>
                      onChange({ ...expansion, pathBase: typeof next === "number" ? next : 0 })
                    }
                  />
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.75 }}>
              Pick the data type shown in the VTS NodeValueLookup log: I32(2) does not match U16(2).
            </div>
            <pre
              aria-label="Floor expansion preview"
              style={{
                margin: "12px 0 0",
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
                background: "rgba(255, 255, 255, 0.06)",
              }}
            >
              {floorTemplateSummary(
                { ...draft, floorExpansion: expansion },
                setup.context,
                setup.spanIsSample,
              )}
            </pre>
            {problem !== null && (
              <div role="alert" style={{ color: "#ff4d4f" }}>
                {problem}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The shared node/attribute/value/message/active form both the "create a new
 * row" section and an in-place row edit render — same fields either way, only
 * the draft, its setter and the tooltips differ. Keeping one copy means a
 * field added here reaches both places instead of only the one that was
 * remembered.
 */
function MappingForm<T extends MappingFormValue>({
  draft,
  onChangeDraft,
  attributeTooltip,
  valueTooltip,
  messageTooltip,
  activeTooltip,
  floorTemplate,
  disabled = false,
  range = undefined,
}: {
  draft: T;
  onChangeDraft: (next: T) => void;
  attributeTooltip: React.ReactNode;
  valueTooltip: React.ReactNode;
  messageTooltip: React.ReactNode;
  activeTooltip: React.ReactNode;
  floorTemplate: FloorTemplateSetup;
  disabled?: boolean;
  range?: PositionRangeControl;
}) {
  return (
    <>
      <NodeIdEditor
        node={draft.node}
        onChangeNode={(node) => onChangeDraft({ ...draft, node })}
        disabled={disabled}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-4 mt-2">
          <AttributeSelect
            value={draft.attributeId}
            onChange={(attributeId) => onChangeDraft({ ...draft, attributeId })}
            tooltip={attributeTooltip}
            disabled={disabled}
          />
        </div>
        <DataValueEditor
          value={draft.nodeValue}
          onChange={(nodeValue) => onChangeDraft({ ...draft, nodeValue })}
          valueTooltip={valueTooltip}
          disabled={disabled}
        />
      </div>
      <div className="row mt-2">
        <div className="col-12 col-md-8 mt-2">
          <OpcUaFieldLabel label="LN2 Message" tooltip={messageTooltip} />
          <div style={{ marginTop: 8 }}>
            <SelectLn2Message
              value={draft.msgId}
              // Default a floor-carrying message to floor 0, and drop a staged
              // floor or floor template when the new message cannot carry one.
              onChange={(key) => onChangeDraft(withMessage(draft, key))}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="col-12 col-md-4 mt-3" style={{ textAlign: "center" }}>
          <OpcUaFieldLabel label="Active" tooltip={activeTooltip} />
          <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
            <Switch
              checked={draft.active}
              onChange={(checked) => onChangeDraft({ ...draft, active: checked })}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
      {messageCarriesPosition(draft.msgId) && (
        <>
          {/* A template has no floor of its own, so neither the floor nor a range applies. */}
          {draft.floorExpansion === null && (
            <PositionField
              value={draft.position}
              onChange={(position) => onChangeDraft({ ...draft, position })}
              disabled={disabled}
              range={range}
            />
          )}
          <FloorTemplateField
            draft={draft}
            setup={floorTemplate}
            disabled={disabled}
            onChange={(floorExpansion) => {
              // Mutually exclusive with a range, and the floor is the template's to stamp.
              if (floorExpansion !== null) range?.onToggle(false);
              onChangeDraft({
                ...draft,
                floorExpansion,
                position:
                  floorExpansion === null ? positionForMessage(draft.msgId, draft.position) : null,
              });
            }}
          />
        </>
      )}
    </>
  );
}

/** Raw-node text split into its `ns=`/identifier segments so the reference
 *  line below can color them independently. BrowsePath has no `ns=` prefix
 *  (see `describeUnbindableNode`), so it renders as a single segment. */
export function nodeIdSegments(node: OpcUaNodeIdForm): { ns: string | null; id: string } {
  const full = describeNode(node);
  if (node.identifierKind === "browsepath") return { ns: null, id: full };
  const semicolon = full.indexOf(";");
  if (semicolon === -1) return { ns: null, id: full };
  return { ns: full.slice(0, semicolon), id: full.slice(semicolon + 1) };
}

/** Segment colors for the raw reference line — a fixed palette (not the
 *  category color, which is already used for the card's border/tag above
 *  it), so `ns=`, the identifier, the value and the message key each read as
 *  a distinct piece of the line. The message key shows its enum value by name
 *  (`status:normal:Power`); the stored number is in its tooltip. */
export const RAW_COLOR = {
  ns: "#69c0ff",
  id: "#b37feb",
  value: "#ffc069",
  msg: "#5cdbd3",
  active: "#95de64",
  inactive: "#ff7875",
};

/** Accent of the OPC UA section and its node identifier. */
const OPC_SECTION_COLOR = "#4096ff";

/** One fixed-size slot in the card's metadata row, so every tag lines up. */
const META_TAG_STYLE: React.CSSProperties = {
  width: 160,
  height: 28,
  lineHeight: "26px",
  margin: 0,
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** The message's friendly name without the "Category: " prefix getDisplayName adds. */
export function messageNameOf(msgId: string): string {
  const category = getCategory(msgId);
  const fullName = getDisplayName(msgId);
  const prefix = category ? `${category.label}: ` : "";
  return prefix && fullName.startsWith(prefix) ? fullName.slice(prefix.length) : fullName;
}

/** Tooltip for the identifier part of a NodeId: names just the type the row actually uses (`s=`, `i=`...). */
function nodeIdentifierHelp(node: OpcUaNodeIdForm): string {
  switch (node.identifierKind) {
    case "string":
      return "Node Identifier, S = String";
    case "numeric":
      return "Node Identifier, I = Numeric";
    case "guid":
      return "Node Identifier, G = GUID";
    case "bytestring":
      return "Node Identifier, B = ByteString";
    default:
      return "Node Identifier, BrowsePath";
  }
}

/** What the variant segment of an LN2 key means, for its tooltip. */
const VARIANT_HELP: Record<string, string> = {
  normal:
    "Normal: the message is just the event itself (for example, a door opened). It carries no extra payload.",
  valued:
    "Valued: the message carries an extra value along with the event (for example, a position or a count).",
};

/**
 * The metadata row of a mapping card: message, attribute and floor (or "per
 * floor" for a floor template), each in an identically sized tag. The node and value are not
 * repeated here; they read in full in the OPC UA section below.
 */
function MappingTags({ row }: { row: OpcUaNodeToMessageRow | OpcUaMessageToNodeRow }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      <Tooltip title="Message">
        <Tag color="cyan" style={META_TAG_STYLE}>
          {messageNameOf(row.msgId)}
        </Tag>
      </Tooltip>
      <Tooltip title="Attribute">
        <Tag color="magenta" style={META_TAG_STYLE}>
          {attributeLabel(row.attributeId)}
        </Tag>
      </Tooltip>
      {row.floorExpansion === null ? (
        <Tooltip title="Floor">
          <Tag color={row.position !== null ? "cyan" : "orange"} style={META_TAG_STYLE}>
            {row.position !== null ? `Floor ${row.position}` : "Floor N/A"}
          </Tag>
        </Tooltip>
      ) : (
        <Tooltip title={FLOOR_TEMPLATE_BADGE_TITLE[row.floorExpansion.mode]}>
          <Tag color="geekblue" style={META_TAG_STYLE}>
            per floor
          </Tag>
        </Tooltip>
      )}
    </div>
  );
}

/** A titled, subtly tinted box; `color` drives its border, tint and title. */
function MappingSection({
  title,
  icon,
  color,
  grow,
  children,
}: {
  title: string;
  icon: typeof faNetworkWired;
  color: string;
  /** Relative width: the OPC UA and LN2 sections split the row 7 : 3. */
  grow: number;
  children: React.ReactNode;
}) {
  return (
    <div
      role="group"
      aria-label={title}
      style={{
        flex: `${grow} 1 0`,
        minWidth: 0,
        border: `1px solid ${color}66`,
        background: `${color}14`,
        borderRadius: 6,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "3px 10px",
          color,
          fontSize: 13,
          fontWeight: 600,
          background: `${color}1f`,
          borderBottom: `1px solid ${color}33`,
        }}
      >
        <FontAwesomeIcon icon={icon} className="generalStyles__mrFix" />
        {title}
      </div>
      <div
        style={{
          padding: "16px 20px",
          fontFamily: "monospace",
          fontSize: 16,
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** A small bordered chip, used for the parts of an LN2 key and for the value / active pills. */
function Chip({
  color = undefined,
  tooltip = undefined,
  children,
}: {
  color?: string;
  tooltip?: React.ReactNode;
  children: React.ReactNode;
}) {
  const chip = (
    <span
      style={{
        display: "inline-block",
        padding: "0 8px",
        borderRadius: 4,
        border: `1px solid ${color ?? "rgba(255, 255, 255, 0.25)"}`,
        color: color ?? "inherit",
        background: color ? `${color}1a` : "rgba(255, 255, 255, 0.06)",
      }}
    >
      {children}
    </span>
  );
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
}

/**
 * The body of a mapping card: the OPC UA side and the LN2 side as two separate
 * sections joined by an arrow that points the way the data flows. An inbound
 * row reads OPC UA → LN2; an outbound row reads LN2 → OPC UA, so the arrow
 * stays truthful and the sections swap sides.
 */
function MappingFlow({
  direction,
  row,
}: {
  direction: "nodeToMsg" | "msgToNode";
  row: OpcUaNodeToMessageRow | OpcUaMessageToNodeRow;
}) {
  const [t] = useTranslation("global");
  const { ns, id } = nodeIdSegments(row.node);
  const category = getCategory(row.msgId);
  const msgParts = row.msgId.split(":");
  // The enum value reads as its message name ("Power", not "7"); the number
  // moves to that part's tooltip. A key that doesn't resolve shows as stored.
  const optionLabel = getOptionLabel(row.msgId);
  const partLabels = optionLabel === null ? msgParts : [msgParts[0], msgParts[1], optionLabel];
  // Tooltip per part of the "category:variant:enumValue" key.
  const partTooltips = [
    category ? `Message type: ${category.label}` : undefined,
    VARIANT_HELP[msgParts[1]],
    optionLabel === null
      ? `Message: ${messageNameOf(row.msgId)}`
      : t("general.opcUaMappingMessageEnumHelp", {
          name: messageNameOf(row.msgId),
          value: msgParts[2],
        }),
  ];

  const opcSection = (
    <MappingSection title="OPC UA Message" icon={faNetworkWired} color={OPC_SECTION_COLOR} grow={7}>
      {ns !== null && (
        <>
          <Tooltip title="Node Namespace Index">
            <span style={{ color: RAW_COLOR.ns }}>{ns}</span>
          </Tooltip>
          <span>;</span>
        </>
      )}
      <Tooltip title={nodeIdentifierHelp(row.node)}>
        <span style={{ color: OPC_SECTION_COLOR }}>{id}</span>
      </Tooltip>{" "}
      <Chip color={RAW_COLOR.value}>(value=&quot;{row.nodeValue.value}&quot;)</Chip>
    </MappingSection>
  );
  const ln2Section = (
    <MappingSection title="LN2 Mapping" icon={faDatabase} color={RAW_COLOR.msg} grow={3}>
      {partLabels.map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key -- parts are positional (category:variant:value)
        <React.Fragment key={i}>
          {i > 0 && <span style={{ margin: "0 4px" }}>:</span>}
          <Chip
            color={i === 0 && category ? categoryColor(category.categoryKey) : undefined}
            tooltip={partTooltips[i]}
          >
            {part}
          </Chip>
        </React.Fragment>
      ))}{" "}
      <Chip color={row.active ? RAW_COLOR.active : RAW_COLOR.inactive}>
        ({row.active ? "active" : "inactive"})
      </Chip>
    </MappingSection>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
      {direction === "nodeToMsg" ? opcSection : ln2Section}
      <div
        aria-hidden="true"
        style={{
          flex: "0 0 auto",
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255, 255, 255, 0.08)",
          border: "1px solid rgba(255, 255, 255, 0.25)",
        }}
      >
        <FontAwesomeIcon icon={faArrowRight} />
      </div>
      {direction === "nodeToMsg" ? ln2Section : opcSection}
    </div>
  );
}

/**
 * True when no Node Monitor observes the node an inbound mapping listens on.
 *
 * A mapping and a monitor are joined by `(node, attributeId)` — there is no
 * explicit reference between them. Only comparable identifiers can be judged:
 * a NodeId can't be matched against a BrowsePath by text, so when the only
 * same-attribute monitors use the other identifier kind the answer is "unknown"
 * and no warning is raised, rather than a false positive.
 */
function lacksNodeMonitor(
  monitors: OpcUaNodeMonitorForm[],
  node: OpcUaNodeIdForm,
  attributeId: AttributeId,
): boolean {
  if (!isNodeComplete(node)) return false;
  const sameAttribute = monitors.filter((m) => m.attributeId === attributeId);
  const wanted = describeNode(node);
  if (sameAttribute.some((m) => describeNode(m.node) === wanted)) return false;
  const isPath = node.identifierKind === "browsepath";
  return !sameAttribute.some((m) => (m.node.identifierKind === "browsepath") !== isPath);
}

/**
 * Explains what Node Monitor a mapping needs, using the mapping's own node and
 * attribute so the values can be copied straight into the monitor form. A
 * mapping and a monitor are joined by (node, attribute) only, which is why
 * those two must match exactly and everything else is free.
 */
function MissingMonitorHelpModal({
  open,
  node,
  attributeId,
  onClose,
}: {
  open: boolean;
  node: OpcUaNodeIdForm;
  attributeId: AttributeId;
  onClose: () => void;
}) {
  const code: React.CSSProperties = {
    fontFamily: "monospace",
    padding: "0 6px",
    borderRadius: 4,
    background: "rgba(255, 255, 255, 0.08)",
    overflowWrap: "anywhere",
  };
  return (
    <Modal
      open={open}
      title="Node Monitor needed for this mapping"
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      }
      width={680}
    >
      <p>
        An incoming mapping only fires when a Node Monitor is watching its node. The monitor is
        matched to the mapping by <b>Node</b> and <b>Attribute</b>, so those two must be identical
        to this mapping&apos;s.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 12px" }}>
        <b>Node</b>
        <span>
          <span style={code}>{describeNode(node)}</span>
        </span>
        <b>Attribute</b>
        <span>
          <span style={code}>{attributeLabel(attributeId)}</span>
        </span>
        <b>Mode</b>
        <span>
          <b>Subscribe</b> (the server pushes changes through a subscription, recommended) or{" "}
          <b>Poll</b> (the client reads the node every Poll Interval ms).
        </span>
        <b>Everything else</b>
        <span>Subscription name, intervals and monitoring parameters are your choice.</span>
      </div>
      <p style={{ marginTop: 12, marginBottom: 4 }}>
        <b>How to create it</b>
      </p>
      <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
        <li>Open the Node Monitors panel on this same page.</li>
        <li>
          Fill in the Node (type <span style={code}>{describeNode(node)}</span>) and set Attribute
          to <span style={code}>{attributeLabel(attributeId)}</span>.
        </li>
        <li>
          Choose a Mode. For Subscribe, pick a subscription from the Subscriptions block (or leave
          the default one).
        </li>
        <li>Press Add. This warning disappears once the monitor exists.</li>
      </ol>
    </Modal>
  );
}

/** JSON.stringify can't take a bigint, and a Uint8Array would print as an index map. */
function cdoJsonReplacer(_key: string, value: unknown): unknown {
  if (typeof value === "bigint") return value.toString();
  if (value instanceof Uint8Array) return Array.from(value);
  return value;
}

const JSON_PRE_STYLE: React.CSSProperties = {
  margin: 0,
  maxHeight: "60vh",
  overflow: "auto",
  padding: 12,
  borderRadius: 6,
  fontSize: 13,
  background: "rgba(255, 255, 255, 0.06)",
};

/**
 * Shows the CDO entry a mapping row packs to (the same object
 * `editorToOpcUaMappingT` writes into `mappings`) twice, side by side: as it is
 * written (enums and unions as FlatBuffer numbers) and with those numbers named
 * (each as "Name (number)", so the sent value stays visible).
 */
function CdoJsonModal({
  open,
  direction,
  row,
  onClose,
}: {
  open: boolean;
  direction: "nodeToMsg" | "msgToNode";
  row: OpcUaNodeToMessageRow | OpcUaMessageToNodeRow;
  onClose: () => void;
}) {
  const [t] = useTranslation("global");
  // Built only while open: it packs the whole LN2 message tree.
  const entry = open ? opcUaMappingRowToCdoEntry(direction, row) : null;
  const plain: unknown = entry === null ? null : JSON.parse(JSON.stringify(entry, cdoJsonReplacer));
  const incomplete = "This row is incomplete, so it is not written to the CDO.";
  const raw = plain === null ? incomplete : JSON.stringify(plain, null, 2);
  const named =
    plain === null ? incomplete : JSON.stringify(humanizeCdoEntry(plain, row.msgId), null, 2);
  return (
    <Modal
      open={open}
      title={t(
        direction === "nodeToMsg"
          ? "general.opcUaCdoJsonNodeToMsgTitle"
          : "general.opcUaCdoJsonMsgToNodeTitle",
      )}
      onCancel={onClose}
      width={1200}
      destroyOnClose
      footer={
        <Button type="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: "0 0 calc(50% - 8px)", minWidth: 0 }}>
          <h6>JSON</h6>
          <pre aria-label="JSON" style={JSON_PRE_STYLE}>
            {raw}
          </pre>
        </div>
        <div style={{ flex: "0 0 calc(50% - 8px)", minWidth: 0 }}>
          <h6>JSON VALUES</h6>
          <pre aria-label="JSON VALUES" style={JSON_PRE_STYLE}>
            {named}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

/**
 * One stored mapping row's card — category-colored border (same scheme
 * `CdoLn1MappingEditor.tsx` uses, keyed by the LN2 category, so e.g. a Fault
 * mapping reads with the same color in both editors): a metadata tag row with
 * Edit/Delete, the OPC UA / LN2 `MappingFlow`, and a full-width warning row. Edit opens the shared `MappingModal`
 * populated with this row; the card itself never grows a form, and the other
 * rows stay fully usable.
 */
function MappingCard<T extends MappingFormValue>({
  direction,
  row,
  onEdit,
  onRemove,
  monitors = undefined,
}: {
  direction: "nodeToMsg" | "msgToNode";
  /** When given, the card warns if no Node Monitor observes its node (inbound rows only). */
  monitors?: OpcUaNodeMonitorForm[];
  row: T;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [cdoOpen, setCdoOpen] = useState(false);
  const category = getCategory(row.msgId);
  const catKey = category?.categoryKey ?? "";
  const missingMonitor =
    monitors !== undefined && lacksNodeMonitor(monitors, row.node, row.attributeId);

  return (
    <div
      style={{
        border: `2px solid ${catKey ? categoryBorderColor(catKey) : "rgba(255, 255, 255, 0.15)"}`,
        borderRadius: 6,
        // Was 5px on top and the 8px of `mb-2` below; +5px each.
        marginTop: 10,
        marginBottom: 13,
        padding: 20,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <MappingTags row={row} />
        </div>
        <Tooltip title="Edit">
          <Button
            aria-label="Edit"
            onClick={onEdit}
            className="buttonStyle__29"
            shape="circle"
            icon={<FontAwesomeIcon icon={faPen} />}
          />
        </Tooltip>
        <Tooltip title="CDO JSON">
          {/* antd has no "info" button type; primary is its info blue. */}
          <Button
            aria-label="CDO JSON"
            type="primary"
            shape="circle"
            icon={<FontAwesomeIcon icon={faCode} />}
            onClick={() => setCdoOpen(true)}
          />
        </Tooltip>
        <Popconfirm
          title="Delete this mapping? This cannot be undone."
          onConfirm={onRemove}
          okText="Yes, Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Tooltip title="Delete">
            <Button
              aria-label="Delete"
              type="primary"
              danger
              shape="circle"
              icon={<FontAwesomeIcon icon={faTrash} />}
            />
          </Tooltip>
        </Popconfirm>
      </div>
      <MappingFlow direction={direction} row={row} />
      {missingMonitor && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <div
            role="status"
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              padding: "4px 12px",
              color: "#faad14",
              fontSize: 13,
              border: "1px solid rgba(250, 173, 20, 0.5)",
              background: "rgba(250, 173, 20, 0.1)",
              borderRadius: 6,
            }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} className="generalStyles__mrFix" />
            No Node Monitor observes this node — this mapping may never fire. Add one in Node
            Monitors.
          </div>
          {/* antd has no "info" button type; primary is its info blue. */}
          <Button type="primary" style={{ borderRadius: 5 }} onClick={() => setHelpOpen(true)}>
            <FontAwesomeIcon icon={faCircleQuestion} style={{ marginRight: 8 }} />
            Help
          </Button>
        </div>
      )}
      <CdoJsonModal
        open={cdoOpen}
        direction={direction}
        row={row}
        onClose={() => setCdoOpen(false)}
      />
      <MissingMonitorHelpModal
        open={helpOpen}
        node={row.node}
        attributeId={row.attributeId}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
}

/** null = closed; `index: null` = adding a new row; a number = editing that stored row. */
type MappingModalState = { index: number | null } | null;

/**
 * The modal both "Add new Message" and a card's Edit open. One form, one
 * footer: the draft is blank for an add and populated for an edit, and only an
 * add offers "Create Range" (an edit addresses a single stored row).
 */
function MappingModal<T extends MappingFormValue>({
  open,
  editing,
  draft,
  onChangeDraft,
  attributeTooltip,
  valueTooltip,
  messageTooltip,
  activeTooltip,
  floorTemplate,
  range = undefined,
  canSubmit,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  editing: boolean;
  draft: T;
  onChangeDraft: (next: T) => void;
  attributeTooltip: React.ReactNode;
  valueTooltip: React.ReactNode;
  messageTooltip: React.ReactNode;
  activeTooltip: React.ReactNode;
  floorTemplate: FloorTemplateSetup;
  range?: PositionRangeControl;
  canSubmit: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      title={editing ? "Edit Message" : "Add new Message"}
      onCancel={onCancel}
      destroyOnClose
      maskClosable={false}
      width={900}
      footer={
        <div className="row">
          <div className="col-9">
            <Button
              onClick={onSubmit}
              disabled={!canSubmit}
              className={`generalStyles__width100 ${canSubmit ? "buttonStyle__28" : ""}`}
            >
              {editing ? (
                "Save"
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                  Add
                </>
              )}
            </Button>
          </div>
          <div className="col-3">
            <Button onClick={onCancel} danger className="generalStyles__width100">
              Cancel
            </Button>
          </div>
        </div>
      }
    >
      <MappingForm
        draft={draft}
        onChangeDraft={onChangeDraft}
        attributeTooltip={attributeTooltip}
        valueTooltip={valueTooltip}
        messageTooltip={messageTooltip}
        activeTooltip={activeTooltip}
        floorTemplate={floorTemplate}
        range={range}
      />
    </Modal>
  );
}

type SortOrder = "asc" | "desc";

/** The Message Type tag's text for a row (the LN2 category label); "" when the message has none. */
function messageTypeLabel(msgId: string): string {
  return getCategory(msgId)?.label ?? "";
}

/**
 * Filter + sort view over one mapping list. Rows keep their ORIGINAL index —
 * edit/delete/dim all address the stored array, so the view must never
 * renumber them. Sorting is by the Message Type tag (stable, so rows of one
 * type keep their stored order).
 */
function useMappingListView<T extends { msgId: string }>(rows: T[]) {
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<SortOrder | undefined>(undefined);
  const types = Array.from(new Set(rows.map((r) => messageTypeLabel(r.msgId)).filter(Boolean)));
  types.sort((a, b) => a.localeCompare(b));
  // A filter whose last row was deleted or re-typed would hide everything.
  const activeFilter = filter !== undefined && types.includes(filter) ? filter : undefined;
  const visible = rows
    .map((row, index) => ({ row, index }))
    .filter(
      ({ row }) => activeFilter === undefined || messageTypeLabel(row.msgId) === activeFilter,
    );
  if (order) {
    const dir = order === "asc" ? 1 : -1;
    visible.sort(
      (a, b) => dir * messageTypeLabel(a.row.msgId).localeCompare(messageTypeLabel(b.row.msgId)),
    );
  }
  const clearFilters = () => {
    setFilter(undefined);
    setOrder(undefined);
  };
  return { types, activeFilter, setFilter, order, setOrder, clearFilters, visible };
}

/** Search-by-Message-Type select plus an asc/desc sort and the "Add new Message" button, shown right above a mapping list. */
function MappingListControls({
  view,
  onAdd,
}: {
  view: ReturnType<typeof useMappingListView>;
  onAdd: () => void;
}) {
  return (
    <div className="row mb-2">
      <div className="col-12 col-md-5 mt-2">
        <Select
          showSearch
          allowClear
          size="large"
          style={{ width: "100%" }}
          placeholder="Search by Message Type"
          aria-label="Filter by Message Type"
          value={view.activeFilter}
          options={view.types.map((type) => ({ value: type, label: type }))}
          onChange={(value) => view.setFilter(value)}
        />
      </div>
      <div className="col-12 col-md-3 mt-2">
        <Select
          allowClear
          size="large"
          style={{ width: "100%" }}
          placeholder="Sort by Message Type"
          aria-label="Sort by Message Type"
          value={view.order}
          options={[
            { value: "asc", label: "Message Type: A → Z" },
            { value: "desc", label: "Message Type: Z → A" },
          ]}
          onChange={(value) => view.setOrder(value)}
        />
      </div>
      <div className="col-6 col-md-2 mt-2">
        <Button
          size="large"
          className="generalStyles__width100"
          disabled={view.activeFilter === undefined && view.order === undefined}
          onClick={view.clearFilters}
        >
          Clear filters
        </Button>
      </div>
      <div className="col-6 col-md-2 mt-2">
        <Button size="large" className="generalStyles__width100 buttonStyle__28" onClick={onAdd}>
          <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
          Add new Message
        </Button>
      </div>
    </div>
  );
}

/**
 * State behind one create form's "Create Range" switch. The range only exists
 * while the staged message carries a floor, so switching to a message that
 * does not turns it off without a second piece of state to keep in sync. A floor
 * template has no floor of its own to range over, so it turns the range off the
 * same way.
 */
function useFloorRangeControl<T extends MappingFormValue>(
  draft: T,
  rows: T[],
  max: number,
  requirePlaceholder: boolean,
  bounded: boolean,
) {
  const [on, setOn] = useState(false);
  const [end, setEnd] = useState<number | null>(null);
  const active = on && messageCarriesPosition(draft.msgId) && draft.floorExpansion === null;
  const problem = active
    ? floorRangeProblem(draft, draft.position, end, max, requirePlaceholder)
    : null;
  // The placeholder rule is about the Node Identifier, so it must not paint To Floor red.
  const endInvalid = active && floorRangeProblem(draft, draft.position, end, max, false) !== null;
  const replaceCount =
    active && problem === null && end !== null && draft.position !== null
      ? countReplacedByRange(rows, draft.msgId, draft.position, end)
      : 0;
  const control: PositionRangeControl = {
    enabled: on,
    end,
    max,
    bounded,
    min: draft.position ?? 0,
    endInvalid,
    problem,
    replaceCount,
    onToggle: setOn,
    onChangeEnd: setEnd,
  };
  const reset = () => {
    setOn(false);
    setEnd(null);
  };
  return { active, problem, end, control, reset };
}

export default function MappingsEditor({
  nodeToMsg,
  msgToNode,
  onChangeNodeToMsg,
  onChangeMsgToNode,
  monitors = undefined,
  floors = undefined,
  adapterPositionBase = undefined,
  adapterVendorFloorIndexing = undefined,
}: {
  /** Node Monitors of the same adapter; when given, inbound rows without one are flagged. */
  monitors?: OpcUaNodeMonitorForm[];
  /**
   * The adapter's floor count; caps "Create Range" at floor `floors - 1`, and is
   * how many floors a floor template previews. Unknown = wire limit and a
   * labelled sample of floors.
   */
  floors?: number;
  /**
   * The adapter's Position Floor Base, when the caller has it: a non-zero value
   * wins over a template's own first value. Unset = the preview says the
   * adapter's floor numbering was not applied.
   */
  adapterPositionBase?: number;
  /** The adapter's Vendor Floor Indexing (`"one_based"` implies a first value of 1); see `adapterPositionBase`. */
  adapterVendorFloorIndexing?: string;
  nodeToMsg: OpcUaNodeToMessageRow[];
  msgToNode: OpcUaMessageToNodeRow[];
  onChangeNodeToMsg: (rows: OpcUaNodeToMessageRow[]) => void;
  onChangeMsgToNode: (rows: OpcUaMessageToNodeRow[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draftNodeToMsg, setDraftNodeToMsg] = useState<OpcUaNodeToMessageRow>({
    ...BLANK_NODE_TO_MSG,
  });
  const [draftMsgToNode, setDraftMsgToNode] = useState<OpcUaMessageToNodeRow>({
    ...BLANK_MSG_TO_NODE,
  });
  // null = modal closed; { index: null } = authoring a new row; { index: n } =
  // editing stored row n. Either way the same modal form is used.
  const [nodeToMsgModal, setNodeToMsgModal] = useState<MappingModalState>(null);
  const [msgToNodeModal, setMsgToNodeModal] = useState<MappingModalState>(null);
  const editingNodeToMsgIndex = nodeToMsgModal?.index ?? null;
  const editingMsgToNodeIndex = msgToNodeModal?.index ?? null;

  const nodeToMsgView = useMappingListView(nodeToMsg);
  const msgToNodeView = useMappingListView(msgToNode);

  // What a floor template previews: the adapter's floor count (a labelled sample
  // when it is unknown) and, when the caller has them, its floor numbering.
  const floorsKnown = floors !== undefined && Number.isFinite(floors);
  const floorTemplate: FloorTemplateSetup = {
    context: {
      span: floorsKnown ? Math.floor(floors) : SAMPLE_FLOOR_SPAN,
      adapterPositionBase,
      adapterVendorFloorIndexing,
    },
    spanIsSample: !floorsKnown,
  };
  const nodeToMsgTemplateProblem =
    draftNodeToMsg.floorExpansion === null
      ? null
      : floorTemplateProblem(draftNodeToMsg, floorTemplate.context);
  const msgToNodeTemplateProblem =
    draftMsgToNode.floorExpansion === null
      ? null
      : floorTemplateProblem(draftMsgToNode, floorTemplate.context);

  const canAddNodeToMsg =
    isNodeComplete(draftNodeToMsg.node) &&
    draftNodeToMsg.msgId !== "" &&
    !isFloorMissing(draftNodeToMsg) &&
    nodeToMsgTemplateProblem === null;
  const canAddMsgToNode =
    isNodeComplete(draftMsgToNode.node) &&
    draftMsgToNode.msgId !== "" &&
    !isFloorMissing(draftMsgToNode) &&
    msgToNodeTemplateProblem === null;

  const maxFloor = maxFloorPosition(floors, MAX_FLOOR_POSITION);
  // Inbound rows are keyed by node + value, so every floor of a range needs its
  // own node (`{floor}` in the identifier). Outbound rows are keyed by message
  // + floor already, so the placeholder is optional there.
  const rangeNodeToMsg = useFloorRangeControl(
    draftNodeToMsg,
    nodeToMsg,
    maxFloor,
    true,
    floors !== undefined,
  );
  const rangeMsgToNode = useFloorRangeControl(
    draftMsgToNode,
    msgToNode,
    maxFloor,
    false,
    floors !== undefined,
  );

  const closeNodeToMsgModal = () => {
    setNodeToMsgModal(null);
    setDraftNodeToMsg({ ...BLANK_NODE_TO_MSG });
    rangeNodeToMsg.reset();
  };

  const closeMsgToNodeModal = () => {
    setMsgToNodeModal(null);
    setDraftMsgToNode({ ...BLANK_MSG_TO_NODE });
    rangeMsgToNode.reset();
  };

  const onSubmitNodeToMsg = () => {
    if (!canAddNodeToMsg) return;
    if (editingNodeToMsgIndex === null && rangeNodeToMsg.active) {
      const { end } = rangeNodeToMsg;
      const start = draftNodeToMsg.position;
      if (rangeNodeToMsg.problem !== null || end === null || start === null) return;
      onChangeNodeToMsg(
        replaceRange(
          nodeToMsg,
          expandFloorRange(draftNodeToMsg, start, end),
          draftNodeToMsg.msgId,
          start,
          end,
        ),
      );
    } else if (editingNodeToMsgIndex !== null) {
      const updated = [...nodeToMsg];
      updated[editingNodeToMsgIndex] = draftNodeToMsg;
      onChangeNodeToMsg(updated);
    } else {
      onChangeNodeToMsg([...nodeToMsg, draftNodeToMsg]);
    }
    closeNodeToMsgModal();
  };

  const onSubmitMsgToNode = () => {
    if (!canAddMsgToNode) return;
    if (editingMsgToNodeIndex === null && rangeMsgToNode.active) {
      const { end } = rangeMsgToNode;
      const start = draftMsgToNode.position;
      if (rangeMsgToNode.problem !== null || end === null || start === null) return;
      onChangeMsgToNode(
        replaceRange(
          msgToNode,
          expandFloorRange(draftMsgToNode, start, end),
          draftMsgToNode.msgId,
          start,
          end,
        ),
      );
    } else if (editingMsgToNodeIndex !== null) {
      const updated = [...msgToNode];
      updated[editingMsgToNodeIndex] = draftMsgToNode;
      onChangeMsgToNode(updated);
    } else {
      onChangeMsgToNode([...msgToNode, draftMsgToNode]);
    }
    closeMsgToNodeModal();
  };

  const onAddNodeToMsg = () => {
    setDraftNodeToMsg({ ...BLANK_NODE_TO_MSG });
    rangeNodeToMsg.reset();
    setNodeToMsgModal({ index: null });
  };

  const onAddMsgToNode = () => {
    setDraftMsgToNode({ ...BLANK_MSG_TO_NODE });
    rangeMsgToNode.reset();
    setMsgToNodeModal({ index: null });
  };

  const onEditNodeToMsg = (index: number) => {
    setDraftNodeToMsg(nodeToMsg[index]);
    rangeNodeToMsg.reset();
    setNodeToMsgModal({ index });
  };

  const onEditMsgToNode = (index: number) => {
    setDraftMsgToNode(msgToNode[index]);
    rangeMsgToNode.reset();
    setMsgToNodeModal({ index });
  };

  const onRemoveNodeToMsg = (index: number) => {
    onChangeNodeToMsg(nodeToMsg.filter((_, i) => i !== index));
  };

  const onRemoveMsgToNode = (index: number) => {
    onChangeMsgToNode(msgToNode.filter((_, i) => i !== index));
  };

  return (
    <div className="container" style={{ width: "100%", maxWidth: "none" }}>
      {/* NODE -> MESSAGE (inbound: node reaches a value -> emit LN2 message) */}
      <Collapse defaultActiveKey={["node-to-msg"]}>
        <Panel
          header={t("general.opcUaNodeToMessageMappings", { total: nodeToMsg.length })}
          key="node-to-msg"
        >
          <MappingListControls view={nodeToMsgView} onAdd={onAddNodeToMsg} />
          {nodeToMsgView.visible.map(({ row, index }) => (
            <MappingCard
              key={`${row.node.namespaceIndex}-${row.node.identifier}-${row.msgId}-${index}`}
              direction="nodeToMsg"
              monitors={monitors}
              row={row}
              onEdit={() => onEditNodeToMsg(index)}
              onRemove={() => onRemoveNodeToMsg(index)}
            />
          ))}
        </Panel>
      </Collapse>
      <MappingModal
        open={nodeToMsgModal !== null}
        editing={editingNodeToMsgIndex !== null}
        draft={draftNodeToMsg}
        onChangeDraft={setDraftNodeToMsg}
        attributeTooltip={t("general.opcUaMappingAttributeHelp")}
        valueTooltip={t("general.opcUaNodeToMsgValueHelp")}
        messageTooltip={t("general.opcUaNodeToMsgMessageHelp")}
        activeTooltip={t("general.opcUaNodeToMsgActiveHelp")}
        floorTemplate={floorTemplate}
        range={editingNodeToMsgIndex === null ? rangeNodeToMsg.control : undefined}
        canSubmit={canAddNodeToMsg && rangeNodeToMsg.problem === null}
        onSubmit={onSubmitNodeToMsg}
        onCancel={closeNodeToMsgModal}
      />
      <br />
      {/* MESSAGE -> NODE (outbound: LN2 command -> write a value to a node) */}
      <Collapse>
        <Panel
          header={t("general.opcUaMessageToNodeMappings", { total: msgToNode.length })}
          key="msg-to-node"
        >
          <MappingListControls view={msgToNodeView} onAdd={onAddMsgToNode} />
          {msgToNodeView.visible.map(({ row, index }) => (
            <MappingCard
              key={`${row.msgId}-${row.node.namespaceIndex}-${row.node.identifier}-${index}`}
              direction="msgToNode"
              row={row}
              onEdit={() => onEditMsgToNode(index)}
              onRemove={() => onRemoveMsgToNode(index)}
            />
          ))}
        </Panel>
      </Collapse>
      <MappingModal
        open={msgToNodeModal !== null}
        editing={editingMsgToNodeIndex !== null}
        draft={draftMsgToNode}
        onChangeDraft={setDraftMsgToNode}
        attributeTooltip={t("general.opcUaMappingAttributeHelp")}
        valueTooltip={t("general.opcUaMsgToNodeValueHelp")}
        messageTooltip={t("general.opcUaMsgToNodeMessageHelp")}
        activeTooltip={t("general.opcUaMsgToNodeActiveHelp")}
        floorTemplate={floorTemplate}
        range={editingMsgToNodeIndex === null ? rangeMsgToNode.control : undefined}
        canSubmit={canAddMsgToNode && rangeMsgToNode.problem === null}
        onSubmit={onSubmitMsgToNode}
        onCancel={closeMsgToNodeModal}
      />
    </div>
  );
}
